/**
 * Utility functions for exporting diff events to TSV (tab-separated values)
 */

import { ALL_PRICING_EVENT_TAGS, formatTagLabel } from '@/types/pricingEventTags';

interface AnnotationCoordinate {
  x: number;
  y: number;
}

interface Annotation {
  num: number;
  label: string;
  before: AnnotationCoordinate | null;
  after: AnnotationCoordinate | null;
}

interface AnnotationCoordinates {
  cloudinary_before?: string;
  cloudinary_after?: string;
  annotations: Annotation[];
}

interface EventForExport {
  slug: string;
  v_before: string;
  v_after: string;
  description?: string;
  annotation_coordinates?: AnnotationCoordinates | null;
  primary_original_url?: string;
  secondary_original_url?: string;
  tags?: string[];
}

/**
 * Build a viewer URL for a given image and annotation
 * @param imageUrl - The Cloudinary URL for the screenshot
 * @param annotation - The annotation coordinate (x, y)
 * @param caption - The caption/label for the annotation
 * @returns The full viewer URL
 */
export function buildViewerUrl(
  imageUrl: string | null | undefined,
  annotation: AnnotationCoordinate | null | undefined,
  caption: string = ''
): string {
  if (!imageUrl || !annotation) {
    return '';
  }

  // Encode the image URL
  const encodedUrl = encodeURIComponent(imageUrl);

  // Fixed dimensions for viewer
  const width = 800;
  const height = 600;

  // Round coordinates to integers
  const x = Math.round(annotation.x);
  const y = Math.round(annotation.y);

  // Encode caption
  const encodedCaption = encodeURIComponent(caption);

  return `https://pricingsaas.com/viewer?url=${encodedUrl}&w=${width}&h=${height}&x=${x}&y=${y}&caption=${encodedCaption}`;
}

/**
 * Build a combined viewer URL for side-by-side comparison
 * @param imageUrlBefore - The Cloudinary URL for the before screenshot
 * @param imageUrlAfter - The Cloudinary URL for the after screenshot
 * @param annotationBefore - The annotation coordinate for before image
 * @param annotationAfter - The annotation coordinate for after image
 * @param caption - The caption/description for the comparison
 * @param labelBefore - Label for the before image (e.g., version)
 * @param labelAfter - Label for the after image (e.g., version)
 * @returns The full combined viewer URL
 */
export function buildCombinedViewerUrl(
  imageUrlBefore: string | null | undefined,
  imageUrlAfter: string | null | undefined,
  annotationBefore: AnnotationCoordinate | null | undefined,
  annotationAfter: AnnotationCoordinate | null | undefined,
  caption: string = '',
  labelBefore: string = '',
  labelAfter: string = ''
): string {
  if (!imageUrlBefore || !imageUrlAfter || !annotationBefore || !annotationAfter) {
    return '';
  }

  // Encode the image URLs
  const encodedUrl1 = encodeURIComponent(imageUrlBefore);
  const encodedUrl2 = encodeURIComponent(imageUrlAfter);

  // Fixed dimensions for viewer
  const width = 800;
  const height = 600;

  // Round coordinates to integers and apply -20px offset to x coordinates
  const x1 = Math.round(annotationBefore.x - 20);
  const y1 = Math.round(annotationBefore.y);
  const x2 = Math.round(annotationAfter.x - 20);
  const y2 = Math.round(annotationAfter.y);

  // Encode labels and caption
  const encodedCaption = encodeURIComponent(caption);
  const encodedLabel1 = encodeURIComponent(labelBefore);
  const encodedLabel2 = encodeURIComponent(labelAfter);

  return `https://pricingsaas.com/compare-viewer?url1=${encodedUrl1}&url2=${encodedUrl2}&w=${width}&h=${height}&x1=${x1}&y1=${y1}&x2=${x2}&y2=${y2}&label1=${encodedLabel1}&label2=${encodedLabel2}&caption=${encodedCaption}`;
}

/**
 * Convert events to TSV format
 * @param events - Array of events to export
 * @returns TSV string and statistics about missing data
 */
export function eventsToTSV(events: EventForExport[]): { tsv: string; stats: { total: number; withUrls: number; missingData: Array<{ slug: string; reason: string }> } } {
  // TSV header - add tag columns
  const tagHeaders = ALL_PRICING_EVENT_TAGS.map(formatTagLabel);
  const headers = ['slug', 'version_before', 'version_after', 'description', 'viewer_url', ...tagHeaders];

  // Track missing data
  const missingData: Array<{ slug: string; reason: string }> = [];
  let withUrls = 0;

  // TSV rows
  const rows = events.map(event => {
    const slug = escapeTsvValue(event.slug);
    const vBefore = escapeTsvValue(event.v_before);
    const vAfter = escapeTsvValue(event.v_after);
    const description = escapeTsvValue(event.description || '');

    // Build combined viewer URL from annotation_coordinates
    let viewerUrl = '';

    if (!event.annotation_coordinates) {
      missingData.push({ slug: event.slug, reason: 'No annotation_coordinates' });
    } else if (!event.annotation_coordinates.annotations || event.annotation_coordinates.annotations.length === 0) {
      missingData.push({ slug: event.slug, reason: 'No annotations array' });
    } else {
      const firstAnnotation = event.annotation_coordinates.annotations[0];

      // Get image URLs - prefer annotation_coordinates, fallback to event-level URLs
      const imageUrlBefore = event.annotation_coordinates.cloudinary_before || event.primary_original_url;
      const imageUrlAfter = event.annotation_coordinates.cloudinary_after || event.secondary_original_url;

      // Check for missing image URLs
      if (!imageUrlBefore || !imageUrlAfter) {
        missingData.push({
          slug: event.slug,
          reason: `Missing ${!imageUrlBefore ? 'before' : 'after'} image URL`
        });
      } else {
        // Option 1: Use same coordinate for both when one is missing
        const coordBefore = firstAnnotation.before || firstAnnotation.after;
        const coordAfter = firstAnnotation.after || firstAnnotation.before;

        if (!coordBefore || !coordAfter) {
          missingData.push({ slug: event.slug, reason: 'Missing both before and after coordinates' });
        } else {
          // Build URL with available data
          viewerUrl = buildCombinedViewerUrl(
            imageUrlBefore,
            imageUrlAfter,
            coordBefore,
            coordAfter,
            event.description || firstAnnotation.label || '',
            event.v_before,
            event.v_after
          );
          if (viewerUrl) {
            withUrls++;
          }
        }
      }
    }

    // Build row with base columns
    const row = [slug, vBefore, vAfter, description, viewerUrl];

    // Add tag columns
    ALL_PRICING_EVENT_TAGS.forEach(tag => {
      const hasTag = event.tags?.includes(tag) ? 'TRUE' : '';
      row.push(hasTag);
    });

    return row.join('\t');
  });

  // Combine headers and rows
  const tsv = [headers.join('\t'), ...rows].join('\n');

  return {
    tsv,
    stats: {
      total: events.length,
      withUrls,
      missingData
    }
  };
}

/**
 * Escape TSV values (handle tabs, newlines)
 * @param value - Value to escape
 * @returns Escaped TSV value
 */
function escapeTsvValue(value: string): string {
  if (!value) return '';

  // Replace tabs and newlines with spaces
  return value.replace(/\t/g, ' ').replace(/\n/g, ' ').replace(/\r/g, '');
}

/**
 * Download TSV file
 * @param tsvContent - TSV string content
 * @param filename - Name of the file to download
 */
export function downloadTSV(tsvContent: string, filename: string): void {
  // Create a blob with the TSV content
  const blob = new Blob([tsvContent], { type: 'text/tab-separated-values;charset=utf-8;' });

  // Create a temporary download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL
  URL.revokeObjectURL(url);
}

/**
 * Generate filename from period filter
 * @param periodFilter - Period filter (e.g., '2026W01', '2025Q4', '2025Y', or '')
 * @returns Filename for the TSV export
 */
export function generateFilename(periodFilter: string): string {
  if (!periodFilter) {
    return 'all_events.tsv';
  }

  // Return period as-is with .tsv extension
  // Examples: 2026W01.tsv, 2025Q4.tsv, 2025Y.tsv
  return `${periodFilter}.tsv`;
}

/**
 * Export events to TSV and trigger download
 * @param events - Array of events to export
 * @param periodFilter - Current period filter for filename
 * @returns Statistics about the export
 */
export function exportEventsToTSV(events: EventForExport[], periodFilter: string): { total: number; withUrls: number; missingData: Array<{ slug: string; reason: string }> } {
  const { tsv, stats } = eventsToTSV(events);
  const filename = generateFilename(periodFilter);
  downloadTSV(tsv, filename);

  // Log missing data for debugging
  if (stats.missingData.length > 0) {
    console.log('[TSV Export] Events missing viewer URLs:', stats.missingData);
  }

  return stats;
}
